import type { ReactNode } from 'react'
import { formatCreditAmount } from '@ztechapps/ai.common'
import { HorizontalStack, Icon, ShowIf, Spinner } from '@ztechapps/webui.util'
import { CREDIT_DECIMALS } from '../constants'
import styles from './CreditBalanceCard.module.scss'

// The "AI Usage" balance header — coins icon, title, description, and the current balance badge.
// Meant to be dropped inside a caller-owned card header (e.g. `Box.Header`); this component
// renders only the header's contents, not the surrounding card, so each app keeps its own
// card/box structure around it. `actions` renders between the description and the balance badge
// (e.g. a self-service top-up trigger) — omit it when the caller places its own trigger elsewhere.
// While `loading`, `actions` stays rendered and a spinner stands in for the badge.
export type CreditBalanceCardProps = {
  loading: boolean
  balance: number | undefined
  currency: string | undefined
  description: string
  actions?: ReactNode
}

export const CreditBalanceCard = ({ loading, balance, currency, description, actions }: CreditBalanceCardProps) => (
  <>
    <HorizontalStack gap={2} className="align-items-center">
      <Icon name="coins" className={styles.coinsIcon} />
      <div>
        <span className="fw-semibold">AI Usage</span>
        <div>
          <small className="text-muted">{description}</small>
        </div>
      </div>
    </HorizontalStack>
    <HorizontalStack gap={2} className="align-items-center">
      {actions}
      <ShowIf condition={loading}>
        <Spinner size="sm" />
      </ShowIf>
      <ShowIf condition={!loading}>
        <HorizontalStack gap={2} className="align-items-center border rounded px-3 py-2">
          <Icon name="coins" className={styles.coinsIcon} />
          <span className="fw-semibold">
            {formatCreditAmount(balance ?? 0, { decimals: CREDIT_DECIMALS })} {currency ?? 'USD'}
          </span>
        </HorizontalStack>
      </ShowIf>
    </HorizontalStack>
  </>
)
